import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from '../../commons/constants/constants';
import { uploadFiles } from '../../services/uploader.service';
import { PageRequest } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/scene.entity';
import { RootModel } from '../models';
import { storeName as userStore } from './user.model';

export const storeName = 'scenes';

const initialState = {
  visible: false,
  updatingPano: false,
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: {},
};

export const scenes: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setUpdatingPano: produce((state, payload) => {
      state.updatingPano = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof scenes>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filter scenes');
      } finally {
        this.setLoading(false);
      }
    },
    async loadById(id) {
      this.setLoading(true);
      try {
        return await dispatch[entityStore].loadById(id);
      } catch (e) {
        console.error(e);
        message.error(`Failed load scene ${id}`);
      } finally {
        this.setLoading(false);
      }
    },
    async saveAsync(payload, state) {
      this.setLoading(true);
      let item;
      try {
        const { user } = state[userStore];
        const params = { ...payload };
        const uploadActions: Promise<{ name: string; filesList: any[] }>[] = [];
        const { images: imageFiles } = params;
        if (imageFiles && !!imageFiles.length) {
          uploadActions.push(
            uploadFiles(params.id, imageFiles, FILE_NAME_TYPE.PANO, user.id, 'scenes', false, true)
          );
        }
        const files = await axios.all(uploadActions);
        delete params.images;
        files.forEach((file) => {
          const { filesList, name } = file;
          if (!(name && filesList.length)) {
            return;
          }
          const { id, url } = filesList[0];
          if (name === FILE_NAME_TYPE.PANO) {
            params.image = { id, url };
          }
        });
        item = await dispatch[entityStore].save(params);

        if (!params.id) {
          this.resetFilter();
        } else {
          this.reloadList();
        }

        message.success('Success to save scene');
      } catch (e) {
        console.error(e);
        message.error(`Fail to save scene`);
      } finally {
        this.setLoading(false);
      }
      return item;
    },
    async delete(id) {
      this.setLoading(true);
      try {
        const scene = await dispatch[entityStore].loadById(id);
        if (!scene) {
          message.error(`Cannot find scene with id ${id}`);
          return;
        }

        const tours = await this.checkSceneUsage(id);
        if (tours && tours.length) {
          const tourNames = tours.map((t) => t.code).join(', ');
          message.warn(`Scene ${scene.code} is currently used by tour${tours.length === 1 ? '' : 's'} ${tourNames}`);
          return;
        }

        await dispatch[entityStore].delete(id);
        await this.reloadList();
        message.success('Success to delete scene');
      } catch (e) {
        console.error(e);
        message.error('Fail to delete scene');
      } finally {
        this.setLoading(false);
      }
    },
    async deleteByIds(ids) {
      this.setLoading(true);
      try {
        await dispatch[entityStore].deleteMultiple(ids);
        await this.reloadList();
        message.success(`Success to delete scenes ${ids.join(', ')}`);
        return true;
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete scenes ${ids.join(', ')}`);
        return false;
      } finally {
        this.setLoading(false);
      }
    },
    async retryCreateScene(id) {
      try {
        await dispatch[entityStore].retry(id);
        message.success('Your request is being processed.');
      } catch (e) {
        console.error(e);
        message.error(`Retry error: ${e.message}`);
      }
    },
    async checkSceneUsage(id) {
      try {
        return await dispatch[entityStore].checkUsage(id);
      } catch (e) {
        console.error(e);
        message.error(`Fail to check scene usage`);
      }
    },
    async updatePano(payload, state) {
      this.setLoading(true);
      let item;
      try {
        const { user } = state[userStore];
        const params = { ...payload };
        const uploadActions: Promise<{ name: string; filesList: any[] }>[] = [];
        const { images: imageFiles } = params;
        if (imageFiles && !!imageFiles.length) {
          uploadActions.push(
            uploadFiles(params.id, imageFiles, FILE_NAME_TYPE.PANO, user.id, 'scenes', false, true)
          );
        }
        const files = await axios.all(uploadActions);
        delete params.images;
        files.forEach((file) => {
          const { filesList, name } = file;
          if (!(name && filesList.length)) {
            return;
          }
          const { id, url } = filesList[0];
          if (name === FILE_NAME_TYPE.PANO) {
            params.image = { id, url };
          }
        });
        item = await dispatch[entityStore].updatePano(params);

        if (!params.id) {
          this.resetFilter();
        } else {
          this.reloadList();
        }

        message.success('Success to update scene');
      } catch (e) {
        console.error(e);
        message.error(`Fail to update scene`);
      } finally {
        this.setLoading(false);
      }
      return item;
    },
  }),
});

export default scenes;
